import { useQueryClient } from '@tanstack/react-query'
import { SlidersHorizontal } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import Button from '@/components/ui/Button'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import Pagination from '@/components/ui/Pagination'
import Skeleton from '@/components/ui/Skeleton'
import {
  screenerEveryQueryOptions,
  useScreenerCount,
  useScreenerEvery,
  useScreenerMetrics,
  useScreenerPage,
  useScreenerSectors,
} from '@/features/screener/api/queries'
import AppliedFilters, { type AppliedTag } from '@/features/screener/components/AppliedFilters'
import ColumnSetTabs from '@/features/screener/components/ColumnSetTabs'
import FiltersDialog from '@/features/screener/components/FiltersDialog'
import ScreenerChartView from '@/features/screener/components/ScreenerChartView'
import ScreenerHeatmap from '@/features/screener/components/ScreenerHeatmap'
import ScreenerTable from '@/features/screener/components/ScreenerTable'
import ViewMenu from '@/features/screener/components/ViewMenu'
import { columnSets, pickSet, type ColumnSet } from '@/features/screener/lib/column-sets'
import { rangeText, serializeFilters, serializeSort } from '@/features/screener/lib/filters'
import {
  NO_FILTER,
  SCREENER_PAGE_SIZES,
  filterOf,
  toScreenQuery,
  type ScreenState,
} from '@/features/screener/lib/screenState'
import { downloadCsv, toCsv } from '@/features/trading-history/lib/tradingHistory'
import { useOnline } from '@/hooks/useOnline'
import { formatQuantity } from '@/lib/format'
import { cn } from '@/lib/utils'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { Metric, ScreenerRow, ScreenerSector, ScreenFilter } from '@/schemas/screener'

const NO_MATCH = 'Nothing matches these filters. Clear one to widen the list.'

const SECTOR_TAG = 'sector'

/** The screener's blocks, 24 apart: the pages revamp's one gap between a page's blocks. */
const STACK = 'flex flex-col gap-6'

/**
 * The loaded screener, block for block: the toolbar's buttons at the right, the set tabs and the
 * view menu, the table panel on two-line rows (ten, the opening page size), and the pager. The
 * route's pending page draws it, and so does the screener itself until the metric list lands.
 */
export function AdvancedScreenerSkeleton({ className }: { className?: string | undefined }) {
  return (
    <div className={cn(STACK, className)}>
      <div className="flex justify-end gap-2">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-30" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-6 w-80 max-w-full" />
          <Skeleton className="h-9 w-28" />
        </div>
        <div>
          <DataTableSkeleton rows={10} columns={7} rowHeight="lg" />
          <div className="mt-6 h-11 lg:h-8" />
        </div>
      </div>
    </div>
  )
}

const sectorName = (sectors: readonly ScreenerSector[] | undefined, key: string): string =>
  sectors?.find((sector) => sector.key.toLowerCase() === key.toLowerCase())?.name ?? key

const appliedTags = (
  metrics: readonly Metric[],
  filter: ScreenFilter,
  sectors: readonly ScreenerSector[] | undefined,
): AppliedTag[] => [
  ...(filter.sector === null
    ? []
    : [{ id: SECTOR_TAG, name: 'Sector', value: sectorName(sectors, filter.sector) }]),
  ...metrics.flatMap((metric) => {
    const range = filter.ranges[metric.key]
    return range === undefined
      ? []
      : [{ id: metric.key, name: metric.label, value: rangeText(metric, range) }]
  }),
]

/** The CSV: Symbol and Company, then the set's own columns, each figure exactly as mm-be sent it. */
const csvOf = (set: ColumnSet, rows: readonly ScreenerRow[]): string =>
  toCsv(
    [
      'Symbol',
      'Company',
      ...set.metrics.map((metric) => metric.label),
      ...(set.withSector ? ['Sector'] : []),
    ],
    rows.map((row) => [
      row.symbol,
      row.name,
      ...set.metrics.map((metric) => row.values[metric.key] ?? ''),
      ...(set.withSector ? [row.sectorName ?? ''] : []),
    ]),
  )

export interface AdvancedScreenerProps {
  /** The screen: filters, sector, order, page, page size, column set and view. */
  state: ScreenState
  /** Every change the member makes, as the whole next state. The host decides where it lives. */
  onStateChange: (next: ScreenState) => void
  /**
   * When the host opens stocks itself (the chart workspace's tab), a row, card or tile hands it
   * the symbol. Absent, each one links to the stock page.
   */
  onOpenSymbol?: ((symbol: string) => void) | undefined
  className?: string | undefined
}

/**
 * The Advanced screener, whole and without page chrome: the toolbar (Filters, Reset, Export CSV),
 * the applied filters, then the matching stocks as a table in one column set, a card per stock or
 * a heatmap of all of them, with the pager and the Filters dialog. Every listed stock that passes
 * the member's ranges on mm-be's screener metrics; the filtering, sorting and paging are mm-be's.
 *
 * Controlled: it holds no screen state of its own, so `/screener/advanced` keeps it in the URL
 * and the chart workspace's tab in its own store. Only passing UI (an open menu, the dialog's
 * draft, the chart range) is local.
 */
export default function AdvancedScreener({
  state,
  onStateChange,
  onOpenSymbol,
  className,
}: AdvancedScreenerProps) {
  const metricsQuery = useScreenerMetrics()

  if (metricsQuery.data === undefined) {
    return metricsQuery.isError ? (
      <EmptyState
        kind="failed"
        size="page"
        title="Couldn’t load the screener"
        onRetry={() => void metricsQuery.refetch()}
        retrying={metricsQuery.isFetching}
        className={className}
      />
    ) : (
      <AdvancedScreenerSkeleton className={className} />
    )
  }

  return (
    <LoadedScreener
      metrics={metricsQuery.data}
      state={state}
      onStateChange={onStateChange}
      onOpenSymbol={onOpenSymbol}
      className={className}
    />
  )
}

function LoadedScreener({
  metrics,
  state,
  onStateChange,
  onOpenSymbol,
  className,
}: AdvancedScreenerProps & { metrics: readonly Metric[] }) {
  const queryClient = useQueryClient()
  const online = useOnline()
  const sets = useMemo(() => columnSets(metrics), [metrics])
  const set = pickSet(sets, state.set)
  const query = toScreenQuery(state, metrics)
  const filter = filterOf(query)

  const page = useScreenerPage(query)
  const total = useScreenerCount(NO_FILTER)
  const sectors = useScreenerSectors()
  const every = useScreenerEvery(filter, query.sort, { enabled: state.view === 'heatmap' })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [dialogKey, setDialogKey] = useState(0)
  const [exporting, setExporting] = useState(false)

  const tags = appliedTags(metrics, filter, sectors.data)
  const price = metrics.find((metric) => metric.key === 'ltp')
  const data = page.data
  // A new filter, order or page holds the old rows while it is asked for: the mock's running
  // skeleton (`Ny`) stands in for them until the new page lands.
  const running = page.isPlaceholderData || (page.isFetching && data === undefined)
  const view = resolveViewState(page, { online })

  const go = (next: Partial<ScreenState>) => {
    onStateChange({ ...state, ...next })
  }

  const applyFilter = (next: ScreenFilter) => {
    go({ f: serializeFilters(next.ranges), sector: next.sector ?? undefined, page: 1 })
  }

  const removeTag = (id: string) => {
    if (id === SECTOR_TAG) {
      applyFilter({ ...filter, sector: null })
      return
    }
    applyFilter({
      ...filter,
      ranges: Object.fromEntries(Object.entries(filter.ranges).filter(([key]) => key !== id)),
    })
  }

  const exportCsv = async () => {
    if (set === undefined) return
    setExporting(true)
    try {
      const rows = await queryClient.query(screenerEveryQueryOptions(filter, query.sort))
      downloadCsv(`screener-${set.id}.csv`, csvOf(set, rows))
    } finally {
      setExporting(false)
    }
  }

  const viewMenu = (
    <ViewMenu
      value={state.view}
      onChange={(next) => {
        go({ view: next })
      }}
    />
  )

  const noMatch = <p className="py-8 text-center text-body-m text-muted-foreground">{NO_MATCH}</p>
  const pager =
    data === undefined ? null : (
      <Pagination
        page={data.page}
        pageSize={state.pageSize}
        total={data.total}
        pageSizes={SCREENER_PAGE_SIZES}
        unit="stocks"
        onPageChange={(next) => {
          go({ page: next })
        }}
        onPageSizeChange={(next) => {
          const size = SCREENER_PAGE_SIZES.find((option) => option === next)
          if (size !== undefined) go({ pageSize: size, page: 1 })
        }}
      />
    )

  let body: ReactNode
  if ((view === 'failed' || view === 'offline') && data === undefined) {
    body = (
      <EmptyState
        kind={view}
        size="page"
        onRetry={() => void page.refetch()}
        retrying={page.isFetching}
        meta={view === 'failed' ? lastUpdatedMeta(page.dataUpdatedAt) : undefined}
        {...(view === 'failed' ? { title: 'Couldn’t run the screen' } : {})}
      />
    )
  } else if (state.view === 'chart') {
    body = (
      <div>
        <ScreenerChartView
          rows={data?.rows ?? []}
          price={price}
          viewMenu={viewMenu}
          onOpenSymbol={onOpenSymbol}
        />
        {data?.total === 0 ? noMatch : pager}
      </div>
    )
  } else if (state.view === 'heatmap') {
    body = every.isError ? (
      <EmptyState
        kind="failed"
        size="section"
        title="Couldn’t draw the heatmap"
        onRetry={() => void every.refetch()}
        retrying={every.isFetching}
      />
    ) : every.data === undefined ? (
      <Skeleton className="h-130 w-full" />
    ) : every.data.length === 0 ? (
      <div className="flex flex-col gap-3">
        <div className="flex justify-end">{viewMenu}</div>
        {noMatch}
      </div>
    ) : (
      <ScreenerHeatmap
        rows={every.data}
        metrics={metrics}
        viewMenu={viewMenu}
        onOpenSymbol={onOpenSymbol}
      />
    )
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <div className="relative z-10 flex items-center gap-4">
          <ColumnSetTabs
            className="flex-1"
            options={sets.map((option) => ({ value: option.id, label: option.tabLabel }))}
            value={set?.id ?? ''}
            onChange={(next) => {
              go({ set: next })
            }}
          />
          <div className="ml-auto shrink-0">{viewMenu}</div>
        </div>
        {set === undefined ? null : (
          <div>
            <ScreenerTable
              set={set}
              rows={data?.rows ?? []}
              sort={query.sort}
              onSortChange={(next) => {
                go({ sort: serializeSort(next), page: 1 })
              }}
              running={running}
              pageSize={state.pageSize}
              empty={<EmptyState kind="no-results" size="section" description={NO_MATCH} />}
              onOpenSymbol={onOpenSymbol}
            />
            {pager}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn(STACK, className)}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="secondary"
            aria-haspopup="dialog"
            className={cn('max-md:flex-1', tags.length > 0 && 'border-border-accent')}
            onClick={() => {
              setDialogKey((previous) => previous + 1)
              setFiltersOpen(true)
            }}
          >
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            Filters
            {tags.length > 0 ? (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-body-xs font-semibold text-accent-foreground">
                {tags.length}
              </span>
            ) : null}
          </Button>
          {tags.length > 0 ? (
            <Button
              variant="quiet"
              className="px-1"
              onClick={() => {
                applyFilter(NO_FILTER)
              }}
            >
              Reset
            </Button>
          ) : null}
          <Button
            variant="secondary"
            className="lg:w-30"
            disabled={exporting || set === undefined}
            aria-busy={exporting || undefined}
            onClick={() => void exportCsv()}
          >
            Export CSV
          </Button>
        </div>
        <AppliedFilters tags={tags} onRemove={removeTag} />
      </div>

      <p className="sr-only" aria-live="polite">
        {data === undefined
          ? ''
          : `${formatQuantity(data.total)} of ${total.data === undefined ? '' : `${formatQuantity(total.data)} `}companies`}
      </p>

      {body}

      <FiltersDialog
        key={dialogKey}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        sets={sets}
        metrics={metrics}
        sectors={sectors.data ?? []}
        applied={filter}
        total={total.data}
        onApply={(next) => {
          applyFilter(next)
          setFiltersOpen(false)
        }}
      />
    </div>
  )
}
